import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { IconProvider } from '@/components/ui';
import { DialogOption, DialogVariable } from '@/lib/stores/dialogStore';
import { Input } from '@/components/ui/InputDefault';
import { TextAreaElement } from '@/components/wizards/wizard-elements/TextAreaElement';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  getCachedStringsItems,
  updateTranslationLine,
  createTranslationLine,
} from '@/lib/stores/stringsStore';
import type { StringsItem } from '@/lib/stores/stringsStore';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useDisplayLanguageOptional, resolveStringDisplayText } from '@/contexts/DisplayLanguageContext';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import {
  SourceContextPanel,
  SourceTextSection,
} from '@/components/wizards/shared/source-context-panel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { ConnectionBadge } from './field-editors/ConnectionBadge';

interface DialogOptionEditorProps {
  option: DialogOption;
  optionIndex: number;
  sectionIndex: number;
  fieldId?: string; // Optional for backwards compatibility
  sectionIdentifiers: string[];
  sections?: { section_identifier: string; name: string }[]; // Added for friendly names
  currentDialogId?: string;
  dialogTranslatable: boolean;
  dialogVariables?: DialogVariable[];
  onUpdateOption: (sectionIndex: number, fieldIdOrOptionIndex: string | number, optionIndex: number, updates: Partial<DialogOption>) => void;
  onRemoveOption: (sectionIndex: number, fieldIdOrOptionIndex: string | number, optionIndex: number) => void;
  gameId?: string;
  t: any;
}

export const DialogOptionEditor = React.memo(function DialogOptionEditor({
  option,
  optionIndex,
  sectionIndex,
  fieldId,
  sectionIdentifiers,
  sections,
  currentDialogId,
  dialogTranslatable,
  dialogVariables,
  onUpdateOption,
  onRemoveOption,
  gameId,
  t,
}: DialogOptionEditorProps) {
  const { selectedGame } = useSimplifiedUserData();
  const effectiveGameId = gameId || selectedGame?.id;
  const displayLanguageCtx = useDisplayLanguageOptional();
  const displayLanguage = displayLanguageCtx?.displayLanguage ?? null;
  const { t: tPlatform } = usePlatformTranslation();
  const noTranslationLabel = tPlatform('platform.TMS.no_translation');

  const [locaKey, setLocaKey] = useState<string | null>(null);
  const [displayText, setDisplayText] = useState<string>(option.option_text);
  const [sourceText, setSourceText] = useState<string>(option.option_text);
  const [linkedStringItem, setLinkedStringItem] = useState<StringsItem | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showVariableAction, setShowVariableAction] = useState(!!option.variableAction);
  const [showVisibilityCondition, setShowVisibilityCondition] = useState(!!option.visibilityCondition);

  /** True when editing a translation (non-source language selected AND option is linked to TMS) */
  const isTranslationMode = displayLanguage !== null && !!option.string_id;
  const createdTranslationIdRef = useRef<string | null>(null);

  // Reset created translation ID when language or string changes
  useEffect(() => {
    createdTranslationIdRef.current = null;
  }, [displayLanguage, option.string_id]);

  // Get current section identifier
  const currentSectionIdentifier = React.useMemo(() => {
    if (!sections || sectionIndex >= sections.length) return undefined;
    return sections[sectionIndex]?.section_identifier;
  }, [sections, sectionIndex]);

  // Create a pseudo-field object for ConnectionBadge compatibility
  const pseudoField = useMemo(() => ({
    id: `option-${optionIndex}`,
    next_field: option.next_field,
    next_section: option.next_section,
    next_dialog: option.next_dialog,
    next_dialog_section: option.next_dialog_section,
  }), [option.next_field, option.next_section, option.next_dialog, option.next_dialog_section, optionIndex]);

  // Wrapper function to handle updates from ConnectionBadge
  const handleConnectionUpdate = useCallback((_sectionIdx: number, _fieldIdParam: string, updates: any) => {
    onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, updates);
  }, [onUpdateOption, sectionIndex, fieldId, optionIndex]);

  const sortableId = fieldId 
    ? `option-${sectionIndex}-${fieldId}-${optionIndex}`
    : `option-${sectionIndex}-${optionIndex}`;
    
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Single combined effect for TMS string data and display text resolution.
  // Mirrors the pattern used by useFieldTextState in BaseFieldEditor.tsx.
  useEffect(() => {
   
    if (option.string_id && effectiveGameId) {
      const strings = getCachedStringsItems(effectiveGameId);
      const linkedString = strings.find(s => s.id === option.string_id);
      if (linkedString) {
        setLocaKey(linkedString.abbrev);
        setSourceText(linkedString.source_text);
        setLinkedStringItem(linkedString);
        const resolved = resolveStringDisplayText(linkedString, displayLanguage, noTranslationLabel);
        setDisplayText(resolved.text || option.option_text);
      } else {
        // String ID exists but string not found in cache yet
        setLocaKey(option.string_id);
        setSourceText(option.option_text);
        setDisplayText(option.option_text);
        setLinkedStringItem(null);
      }
    } else {
      setLocaKey(null);
      setSourceText(option.option_text);
      setDisplayText(option.option_text);
      setLinkedStringItem(null);
    }
  }, [option.string_id, option.option_text, effectiveGameId, displayLanguage, noTranslationLabel]);

  // Debounce timeout ref for text updates
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced update function for parent state (translation-aware)
  const debouncedUpdateOption = useCallback((value: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (isTranslationMode && option.string_id && displayLanguage) {
      // Translation mode: save to TMS with longer debounce (API call)
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const existingTranslation = linkedStringItem?.translations?.find(
            (t) => t.language_code === displayLanguage
          );
          const translationId = existingTranslation?.id || createdTranslationIdRef.current;

          if (translationId) {
            await updateTranslationLine(translationId, {
              language_code: displayLanguage,
              translated_text: value,
              approved: false,
            });
          } else {
            const result = await createTranslationLine({
              dialogue_line_id: option.string_id!,
              language_code: displayLanguage,
              translated_text: value,
              approved: false,
            });
            if (result?.id) {
              createdTranslationIdRef.current = result.id;
            }
          }
        } catch (error) {
          console.error('[DialogOptionEditor] Failed to save translation:', error);
        }
      }, 800);
    } else {
      // Source mode: update option text locally
      debounceTimeoutRef.current = setTimeout(() => {
        onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
          ...option,
          option_text: value,
        });
      }, 300);
    }
  }, [onUpdateOption, sectionIndex, fieldId, optionIndex, option, isTranslationMode, displayLanguage, linkedStringItem]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Handle option text update - local state updates immediately, parent update is debounced
  const handleOptionTextChange = useCallback((value: string) => {
    // Update local display immediately for responsive UI
    setDisplayText(value);
    
    // Debounce the parent update
    debouncedUpdateOption(value);
  }, [debouncedUpdateOption]);

  // Flush debounced updates on blur to ensure data is saved (translation-aware)
  const handleOptionTextBlur = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    if (isTranslationMode && option.string_id && displayLanguage) {
      // Translation mode: save to TMS
      const existingTranslation = linkedStringItem?.translations?.find(
        (t) => t.language_code === displayLanguage
      );
      const translationId = existingTranslation?.id || createdTranslationIdRef.current;

      if (translationId) {
        updateTranslationLine(translationId, {
          language_code: displayLanguage,
          translated_text: displayText,
          approved: false,
        }).catch(err => console.error('[DialogOptionEditor] Blur save failed:', err));
      } else {
        createTranslationLine({
          dialogue_line_id: option.string_id!,
          language_code: displayLanguage,
          translated_text: displayText,
          approved: false,
        }).then(result => {
          if (result?.id) {
            createdTranslationIdRef.current = result.id;
          }
        }).catch(err => console.error('[DialogOptionEditor] Blur create failed:', err));
      }
    } else {
      // Source mode: immediately update parent with current display text
      onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
        ...option,
        option_text: displayText,
      });
    }
  }, [onUpdateOption, sectionIndex, fieldId, optionIndex, option, displayText, isTranslationMode, displayLanguage, linkedStringItem]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border rounded p-2 space-y-2 bg-background"
    >
      {/* Option Header */}
      <div className="flex items-center gap-2">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
          type="button"
        >
          <IconProvider iconstring="fa-grip-vertical" size={10} className="text-muted-foreground" />
        </button>
        <span className="text-xs font-medium">
          Option {optionIndex + 1}
        </span>
        {option.next_section && (
          <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto">
            <IconProvider iconstring="fa-arrow-right" size={8} />
            {sections?.find(s => s.section_identifier === option.next_section)?.name || option.next_section}
          </span>
        )}
        <Button
          onClick={() => onRemoveOption(sectionIndex, fieldId || optionIndex, optionIndex)}
          size="sm"
          variant="ghost"
          className="text-destructive h-6 w-6 p-0"
          type="button"
        >
          <IconProvider iconstring="fa-times" size={10} />
        </Button>
      </div>

      {/* Option Text */}
      <div className="flex items-start gap-2">
        <Label className="w-20 text-xs pt-2">Text</Label>
        <div
          className="flex-1"
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setIsFocused(false);
            }
          }}
        >
          {locaKey ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <TextAreaElement
                      value={displayText}
                      onChange={handleOptionTextChange}
                      onBlur={handleOptionTextBlur}
                      placeholder={t('platform.wizards.dialog.enter_option_text')}
                      rows={1}
                      enableBubbleMenu={true}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="font-mono text-xs">
                  <div className="flex items-center gap-1.5">
                    <IconProvider iconstring="fa-link" size={12} />
                    <span>{locaKey}</span>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <TextAreaElement
              value={displayText}
              onChange={handleOptionTextChange}
              onBlur={handleOptionTextBlur}
              placeholder={t('platform.wizards.dialog.enter_option_text')}
              rows={1}
              enableBubbleMenu={true}
            />
          )}

          {/* Floating source context panel — visible when editing a translation */}
          <SourceContextPanel isVisible={isTranslationMode && isFocused && !!sourceText}>
            <SourceTextSection sourceText={sourceText} locaKey={locaKey} />
          </SourceContextPanel>
        </div>
      </div>

      {/* Navigation Selection */}
      <div className="flex items-center gap-2">
        <Label className="text-xs font-semibold min-w-[50px]">Action:</Label>
        <ConnectionBadge
          field={pseudoField as any}
          sectionIndex={sectionIndex}
          sectionIdentifiers={sectionIdentifiers}
          sections={sections}
          currentDialogId={currentDialogId}
          effectiveGameId={effectiveGameId}
          onUpdateField={handleConnectionUpdate}
          showFieldOption={false}
        />
      </div>

      {/* Variable Action Section */}
      <div className="border-t pt-2">
        <button
          onClick={() => setShowVariableAction(!showVariableAction)}
          className="flex items-center gap-2 text-xs font-medium text-purple-700 hover:text-purple-900 w-full"
          type="button"
        >
          <IconProvider iconstring={showVariableAction ? "fa-chevron-down" : "fa-chevron-right"} size={10} />
          <IconProvider iconstring="fa-code" size={10} />
          <span>Variable Action</span>
          {option.variableAction && (
            <span className="ml-auto text-purple-600 font-mono text-[10px]">
              {option.variableAction.variable} {option.variableAction.operator === 'set' ? '=' : option.variableAction.operator === 'increment' ? '+=' : '-='} {String(option.variableAction.value)}
            </span>
          )}
        </button>
        
        {showVariableAction && (() => {
          // Get the selected variable's type
          const selectedVar = dialogVariables?.find(v => v.name === option.variableAction?.variable);
          const varType = selectedVar?.type || 'number';
          const isText = varType === 'text';
          const isBoolean = varType === 'boolean';
          const isRange = varType === 'range';
          const isNumeric = varType === 'number' || isRange;
          
          return (
            <div className="mt-2 space-y-2 bg-purple-50 p-2 rounded border border-purple-200">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-gray-600">When selected, set variable:</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
                      ...option,
                      variableAction: undefined,
                    });
                    setShowVariableAction(false);
                  }}
                  className="h-6 px-2 text-xs text-purple-700 hover:text-purple-900 hover:bg-purple-100"
                  type="button"
                >
                  <IconProvider iconstring="fa-times" size={10} className="mr-1" />
                  Remove
                </Button>
              </div>
              <div className="grid gap-2" style={{ gridTemplateColumns: isText || isBoolean ? '2fr 1fr' : '2fr 1fr 1fr' }}>
                {/* Variable Selector */}
                {dialogVariables && dialogVariables.filter(v => v.name.trim()).length > 0 ? (
                  <Select
                    value={option.variableAction?.variable || ''}
                    onValueChange={(value) => {
                      const selectedVariable = dialogVariables.find(v => v.name === value);
                      const defaultValue = selectedVariable?.type === 'boolean' ? false : 
                                         selectedVariable?.type === 'text' ? '' : 0;
                      onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
                        variableAction: {
                          ...(option.variableAction || {}),
                          variable: value,
                          operator: 'set', // Reset to 'set' when changing variables
                          value: defaultValue
                        }
                      });
                    }}
                  >
                    <SelectTrigger className="h-7 text-xs font-mono">
                      <SelectValue placeholder="Select variable..." />
                    </SelectTrigger>
                    <SelectContent>
                      {dialogVariables.filter(v => v.name.trim()).map((v) => (
                        <SelectItem key={v.name} value={v.name}>
                          {v.name} ({v.type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={option.variableAction?.variable || ''}
                    onChange={(e) => onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
                      variableAction: {
                        ...(option.variableAction || { operator: 'set', value: 0 }),
                        variable: e.target.value
                      }
                    })}
                    placeholder="variable_name"
                    className="h-7 text-xs font-mono"
                  />
                )}
                
                {/* Operator Selector (hidden for text and boolean) */}
                {!isText && !isBoolean && (
                  <select
                    className="h-7 rounded-md border border-input bg-background px-2 text-xs"
                    value={option.variableAction?.operator || 'set'}
                    onChange={(e) => onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
                      variableAction: {
                        ...(option.variableAction || { variable: '', value: 0 }),
                        operator: e.target.value as 'set' | 'increment' | 'decrement'
                      }
                    })}
                  >
                    <option value="set">Set</option>
                    <option value="increment">Increment</option>
                    <option value="decrement">Decrement</option>
                  </select>
                )}
                
                {/* Value Input - varies by type */}
                {isBoolean ? (
                  <Select
                    value={String(option.variableAction?.value ?? false)}
                    onValueChange={(value) => onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
                      variableAction: {
                        ...(option.variableAction || { variable: '', operator: 'set' }),
                        value: value === 'true'
                      }
                    })}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">true</SelectItem>
                      <SelectItem value="false">false</SelectItem>
                    </SelectContent>
                  </Select>
                ) : isText ? (
                  <Input
                    type="text"
                    value={String(option.variableAction?.value ?? '')}
                    onChange={(e) => onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
                      variableAction: {
                        ...(option.variableAction || { variable: '', operator: 'set' }),
                        value: e.target.value
                      }
                    })}
                    placeholder="text value"
                    className="h-7 text-xs font-mono"
                  />
                ) : (
                  <Input
                    type="number"
                    value={option.variableAction?.value !== undefined ? String(option.variableAction.value) : ''}
                    onChange={(e) => {
                      const parsedValue = parseFloat(e.target.value) || 0;
                      onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
                        variableAction: {
                          ...(option.variableAction || { variable: '', operator: 'set' }),
                          value: parsedValue
                        }
                      });
                    }}
                    placeholder={isRange ? `${selectedVar?.rangeMin ?? 0}-${selectedVar?.rangeMax ?? 100}` : "value"}
                    className="h-7 text-xs font-mono"
                    min={isRange ? selectedVar?.rangeMin : undefined}
                    max={isRange ? selectedVar?.rangeMax : undefined}
                    step={isRange ? selectedVar?.increment : undefined}
                  />
                )}
              </div>
              
              {/* Range info */}
              {isRange && selectedVar && (
                <div className="text-xs text-gray-500 italic">
                  Range: {selectedVar.rangeMin ?? 0} to {selectedVar.rangeMax ?? 100}, step: {selectedVar.increment ?? 1}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Visibility Condition Section */}
      <div className="border-t pt-2">
        <button
          onClick={() => setShowVisibilityCondition(!showVisibilityCondition)}
          className="flex items-center gap-2 text-xs font-medium text-blue-700 hover:text-blue-900 w-full"
          type="button"
        >
          <IconProvider iconstring={showVisibilityCondition ? "fa-chevron-down" : "fa-chevron-right"} size={10} />
          <IconProvider iconstring="fa-eye" size={10} />
          <span>Visibility Condition</span>
          {option.visibilityCondition && (
            <span className="ml-auto text-blue-600 font-mono text-[10px]">
              {option.visibilityCondition.variable} {option.visibilityCondition.operator} {String(option.visibilityCondition.value)}
            </span>
          )}
        </button>
        
        {showVisibilityCondition && (
          <div className="mt-2 space-y-2 bg-blue-50 p-2 rounded border border-blue-200">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-gray-600">Show only if:</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
                    ...option,
                    visibilityCondition: undefined,
                  });
                  setShowVisibilityCondition(false);
                }}
                className="h-6 px-2 text-xs text-blue-700 hover:text-blue-900 hover:bg-blue-100"
                type="button"
              >
                <IconProvider iconstring="fa-times" size={10} className="mr-1" />
                Remove
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {dialogVariables && dialogVariables.filter(v => v.name.trim()).length > 0 ? (
                <Select
                  value={option.visibilityCondition?.variable || ''}
                  onValueChange={(value) => onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
                    visibilityCondition: {
                      ...(option.visibilityCondition || { operator: '==', value: false }),
                      variable: value
                    }
                  })}
                >
                  <SelectTrigger className="h-7 text-xs font-mono">
                    <SelectValue placeholder="Select variable..." />
                  </SelectTrigger>
                  <SelectContent>
                    {dialogVariables.filter(v => v.name.trim()).map((v) => (
                      <SelectItem key={v.name} value={v.name}>
                        {v.name} ({v.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={option.visibilityCondition?.variable || ''}
                  onChange={(e) => onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
                    visibilityCondition: {
                      ...(option.visibilityCondition || { operator: '==', value: false }),
                      variable: e.target.value
                    }
                  })}
                  placeholder="variable_name"
                  className="h-7 text-xs font-mono"
                />
              )}
              <select
                className="h-7 rounded-md border border-input bg-background px-2 text-xs"
                value={option.visibilityCondition?.operator || '=='}
                onChange={(e) => onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
                  visibilityCondition: {
                    ...(option.visibilityCondition || { variable: '', value: false }),
                    operator: e.target.value as '==' | '!=' | '>' | '<' | '>=' | '<='
                  }
                })}
              >
                <option value="==">==</option>
                <option value="!=">!=</option>
                <option value=">">&gt;</option>
                <option value="<">&lt;</option>
                <option value=">=">&gt;=</option>
                <option value="<=">&lt;=</option>
              </select>
              <Input
                type="number"
                value={option.visibilityCondition?.value !== undefined ? String(option.visibilityCondition.value) : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  let parsedValue: boolean | number;
                  if (val === 'true' || val === 'false') {
                    parsedValue = val === 'true';
                  } else {
                    parsedValue = parseFloat(val) || 0;
                  }
                  onUpdateOption(sectionIndex, fieldId || optionIndex, optionIndex, {
                    visibilityCondition: {
                      ...(option.visibilityCondition || { variable: '', operator: '==' }),
                      value: parsedValue
                    }
                  });
                }}
                placeholder="value"
                className="h-7 text-xs font-mono"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

